import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { app } from '../firebase';

const functions = getFunctions(app, 'europe-west1');

if (process.env.NODE_ENV === 'development') {
  try {
    connectFunctionsEmulator(functions, 'localhost', 5001);
  } catch {
    // Already connected
  }
}

class SpirisAuth {
  constructor() {
    this.getConfigFunction = httpsCallable(functions, 'getSpirisOAuthConfig');
    this.exchangeCodeFunction = httpsCallable(functions, 'exchangeSpirisCode');
    this.getStatusFunction = httpsCallable(functions, 'getSpirisStatus');
    this.verifyFunction = httpsCallable(functions, 'verifySpirisConnection');
    this.refreshTokenFunction = httpsCallable(functions, 'refreshSpirisToken');
    this.syncDataFunction = httpsCallable(functions, 'syncSpirisData', { timeout: 540000 });
  }

  generateSecureState() {
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
      const array = new Uint32Array(4);
      window.crypto.getRandomValues(array);
      return Array.from(array, (dec) => dec.toString(36)).join('');
    }
    return Math.random().toString(36).slice(2, 15) + Math.random().toString(36).slice(2, 15) + Date.now().toString(36);
  }

  async getAuthorizationUrl(state = null) {
    const configResult = await this.getConfigFunction();
    const config = configResult.data || {};
    const clientId = config.clientId || '';
    const redirectUri = config.redirectUri
      || (typeof window !== 'undefined' ? `${window.location.origin}/spiris/callback` : 'https://app.svenry.com/spiris/callback');
    const scope = config.scope || 'ea:api offline_access ea:sales_readonly ea:purchase_readonly ea:accounting_readonly';
    const authorizeUrl = config.authorizeUrl || 'https://identity.vismaonline.com/connect/authorize';
    const prompt = config.prompt || 'select_account';
    const acrValues = config.acrValues || 'service:44643EB1-3F76-4C1C-A672-402AE8085934';

    if (!clientId) {
      throw new Error('Spiris Client ID not configured. Add SPIRIS_CLIENT_ID to Secret Manager.');
    }

    const stateValue = state || this.generateSecureState();
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem('spiris_oauth_state', stateValue);
      sessionStorage.setItem('spiris_oauth_redirect_uri', redirectUri);
    }

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectUri,
      scope,
      state: stateValue,
      prompt,
      acr_values: acrValues,
    });

    return `${authorizeUrl}?${params.toString()}`;
  }

  async exchangeCodeForToken(code, state) {
    const redirectUri = (typeof window !== 'undefined' && window.sessionStorage)
      ? sessionStorage.getItem('spiris_oauth_redirect_uri')
      : null;
    const result = await this.exchangeCodeFunction({
      code,
      redirectUri: redirectUri || undefined,
      state,
    });
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem('spiris_oauth_redirect_uri');
    }
    return result.data;
  }

  async getConnectionStatus() {
    const result = await this.getStatusFunction();
    return result.data;
  }

  async verifyConnection() {
    const result = await this.verifyFunction();
    return result.data;
  }

  async refreshAccessToken() {
    const result = await this.refreshTokenFunction();
    return result.data;
  }

  async syncData() {
    const result = await this.syncDataFunction();
    return result.data;
  }

  async connect() {
    const state = this.generateSecureState();
    window.location.href = await this.getAuthorizationUrl(state);
  }
}

const spirisAuth = new SpirisAuth();
export default spirisAuth;
